import { FC, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';

import CircleDollar from 'dibs-icons/exports/legacy/CircleDollar';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import { trackPricingGuidanceComparePricesClick } from '../PdpSharedPricingGuidance/pricingGuidanceTracking';
import { showSoldPricingGuidance } from '../PdpSharedPricingGuidance/pricingGuidanceHelpers';
import { PdpSharedPricingGuidanceSoldLink } from '../PdpSharedPricingGuidance/PdpSharedPricingGuidanceSoldLink';
import { useSoldItemLinksPdpClientVariant } from '../hooks/useSoldItemLinksPdpClientVariant';

import { PdpRespSidebarBlurbPricingGuidance_item$key } from './__generated__/PdpRespSidebarBlurbPricingGuidance_item.graphql';

import styles from './PdpRespSidebarBlurbPricingGuidance.scss';

const itemFragment = graphql`
    fragment PdpRespSidebarBlurbPricingGuidance_item on Item {
        pricingGuidance {
            priceRange
        }
        ...pricingGuidanceHelpers_item
        ...PdpSharedPricingGuidanceSoldLink_item
        ...useSoldItemLinksPdpClientVariant_item
    }
`;

export const PdpRespSidebarBlurbPricingGuidance: FC<{
    item: PdpRespSidebarBlurbPricingGuidance_item$key;
    currency: string;
    skuId?: string | null;
}> = ({ item: itemRef, currency, skuId }) => {
    const item = useFragment(itemFragment, itemRef);
    const { pricingGuidance } = item;

    const isSoldItemLinksPdpClientVariant = useSoldItemLinksPdpClientVariant({ item });

    const onClick = useCallback(() => {
        trackPricingGuidanceComparePricesClick(pricingGuidance?.priceRange || null);
    }, [pricingGuidance]);

    return isSoldItemLinksPdpClientVariant && showSoldPricingGuidance({ item, currency }) ? (
        <PdpSharedBlurb zeroPadding IconComponent={CircleDollar} iconClass={styles.icon}>
            <FormattedMessage
                id="PdpRespSidebarBlurbPricingGuidance.link"
                defaultMessage="<bold>Pricing Guidance:</bold> View similar sold items <link>Compare Prices</link>"
                values={{
                    bold: chunks => <strong>{chunks}</strong>,
                    link: chunks => (
                        <PdpSharedPricingGuidanceSoldLink
                            item={item}
                            onClick={onClick}
                            skuId={skuId}
                        >
                            {chunks}
                        </PdpSharedPricingGuidanceSoldLink>
                    ),
                }}
            />
        </PdpSharedBlurb>
    ) : null;
};
